import { window } from "global";
import React, { useState, useEffect, useMemo, useRef } from "react";
import classnames from "classnames";
import FilterModal from "../FilterModal";
import { ReactComponent as IconX } from "svg/x.svg";
import { ReactComponent as TriangleArrowDown } from "svg/triangle-arrow-down.svg";
import gql from "graphql-tag";
import { useLazyQuery } from "@apollo/client";
import { getSummonerProfileContext } from "components/SummonerProfiles/SummonerProfileContext";

const FETCH_PLAYED_WITH = gql`
  query FetchPlayedWith($regionId: String!, $summonerName: String!) {
    fetchPlayedWith(regionId: $regionId, summonerName: $summonerName) {
      summonerNames
    }
  }
`;

const Summoner = ({
  className,
  value,
  onFilterChange,
  filterKey = "duoName",
  hideDropdownIndicator,
  customButton,
  isMobile,
  fontSize,
}) => {
  const { summonerName, region } = getSummonerProfileContext();

  const [getSummoners, { loading, data }] = useLazyQuery(FETCH_PLAYED_WITH, {
    ssr: false,
    variables: {
      regionId: region,
      summonerName,
    },
  });

  const filterRef = useRef();
  const [inputActive, setInputActive] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [highlightedItem, setHighlightedItem] = useState(0);

  const { fetchPlayedWith } = data || {};
  const { summonerNames } = fetchPlayedWith || {};

  const allSummoners = summonerNames
    ? [...summonerNames].sort().map((playedWith) => ({
        name: playedWith,
        value: playedWith,
      }))
    : [];

  const mobileOptions = allSummoners.map((summoner) => ({
    label: summoner.name,
    value: summoner.value,
  }));

  allSummoners.unshift({
    name: "All Summoners",
    value: null,
  });

  mobileOptions.unshift({
    label: "All Summoners",
    value: null,
  });

  const resetFilter = () => {
    setInputActive(false);
    setInputValue("");
    setHighlightedItem(0);
  };

  useEffect(() => {
    if (window && filterRef.current && inputActive) {
      const outsideClick = (e) => {
        if (filterRef.current && e.target !== null && !filterRef.current.contains(e.target)) {
          resetFilter();
        }
      };
      window.addEventListener("click", outsideClick);

      return () => window.removeEventListener("click", outsideClick);
    }
  }, [inputActive]);

  useEffect(() => {
    setHighlightedItem(0);
  }, [inputValue]);

  const onClick = (e) => {
    !inputActive && setInputActive(true);
    getSummoners();
  };

  const onFilterSelect = (summoner) => {
    onFilterChange(filterKey, summoner);
    resetFilter();
  };

  const onChange = (e) => {
    setInputValue(e.target.value);
  };

  const summonerList = useMemo(() => {
    if (!inputValue.trim()) return allSummoners;

    return allSummoners.filter((summoner) => {
      const normalizedSummonerName = summoner.name.toLowerCase().replace(/[^A-Za-z0-9]/g, "");
      const normalizedInput = inputValue.toLowerCase().replace(/[^A-Za-z0-9]/g, "");
      return normalizedSummonerName.startsWith(normalizedInput);
    });
  }, [data, inputValue]);

  const onKeyDown = (e) => {
    if (e.which === 13) {
      // enter
      onFilterSelect(summonerList[highlightedItem]);
    } else if (e.which === 38) {
      // up
      e.preventDefault();
      setHighlightedItem(Math.abs(summonerList.length + highlightedItem - 1) % summonerList.length);
    } else if (e.which === 40) {
      // down
      e.preventDefault();
      setHighlightedItem(Math.abs(summonerList.length + highlightedItem + 1) % summonerList.length);
    }
  };

  const duoName = value;

  const FilterComponent = (
    <React.Fragment>
      {
        // Need this because clicks don't trigger on parent <div>
        isMobile && (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            }}
          ></div>
        )
      }
      <div ref={filterRef} className={classnames("filter-select", "filter-select_champion", className)} style={{ fontSize }}>
        <div className="champion-control" onClick={onClick}>
          {/*<span className="vs">vs.</span>*/}
          <div className="champion-label">
            {inputActive ? (
              <input
                style={{ fontSize }}
                placeholder="Search summoner"
                value={inputValue}
                onChange={onChange}
                onKeyDown={onKeyDown}
                autoFocus
              />
            ) : (
              <div className="champion">
                {duoName ? (
                  <React.Fragment>
                    <div className="champion-name">{duoName}</div>
                  </React.Fragment>
                ) : (
                  <span>All Summoners</span>
                )}
              </div>
            )}
          </div>
          {!inputActive && !hideDropdownIndicator && <TriangleArrowDown className="custom-indicator" />}
          {!isMobile && duoName && !inputActive && (
            <div
              className="clear-champion"
              onClick={(e) => {
                e.stopPropagation();
                onFilterChange(filterKey, { value: null });
              }}
            >
              <IconX className="x-icon" />
            </div>
          )}
        </div>
        {inputActive && summonerList.length > 0 && (
          <div className="champion-menu">
            {loading ? (
              <div style={{ padding: "2rem" }}>
                <div className="spinthatshit-loader">
                  <div className="spinner"></div>
                </div>
              </div>
            ) : (
              summonerList.map((summoner, index) => {
                const className = classnames("champion-menu_item", {
                  highlighted: index === highlightedItem,
                });
                return (
                  <div
                    key={summoner.value}
                    className={className}
                    onMouseEnter={() => setHighlightedItem(index)}
                    onClick={() => onFilterSelect(summoner)}
                  >
                    <div className="champion-name">{summoner.name}</div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );

  return (
    <div className="filter-select-wrapper">
      {isMobile ? (
        <FilterModal
          button={customButton || FilterComponent}
          title={"Change Summoner"}
          group={"summoner"}
          options={mobileOptions}
          curValue={{
            label: duoName,
            value: duoName,
          }}
          loading={loading}
          onApply={(selected) => onFilterChange(filterKey, selected)}
          onToggleModal={() => getSummoners()}
        />
      ) : (
        FilterComponent
      )}
    </div>
  );
};

export default Summoner;
